import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { DashboardDeregistrationQuestions } from "@/components/non_registrant/dashboard-deregistration-questions";
import { DashboardInspectionQuestions } from "@/components/non_registrant/dashboard-inspection-questions";
import { DashboardQuestions } from "@/components/non_registrant/dashboard-questions";
import { QuestionGroupConfigurationPanel } from "@/components/non_registrant/question-group-configuration-panel";
import PageHeader from "@/components/page-header";
import {
  SETUP_PAGE_CHROME_CLASS,
  SETUP_PAGE_LIST_TAB_CLASS,
  SETUP_PAGE_SHELL_CLASS,
  SETUP_PAGE_TABS_CLASS,
} from "@/components/portal/setup-page-layout";
import {
  SETUP_TABS_LIST_CLASS,
  SETUP_TAB_TRIGGER_CLASS,
  SetupPageTabBar,
} from "@/components/portal/setup-page-tab-bar";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseTypeFilterOptions } from "@/hooks/use-license-type-filter-options";
import { useOperatorDashboardStats } from "@/hooks/use-operator-dashboard-stats";
import { DASHBOARD_STATS_TAB_PRELOAD_ALL } from "@/lib/dashboard-query-options";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { getDashboardLink } from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/operator/question-configuration")({
  validateSearch: (search: Record<string, unknown>) => {
    const tab = (search.tab as string) || DEFAULT_TAB;
    const validTab = (QUESTION_TAB_VALUES as readonly string[]).includes(tab)
      ? tab
      : DEFAULT_TAB;
    return { tab: validTab };
  },
  component: OperatorQuestionConfigurationPage,
});

const QUESTION_TABS = [
  { value: "registration-groups", label: "Registration Groups" },
  { value: "deregistration-groups", label: "De-registration Groups" },
  { value: "inspection-groups", label: "Inspection Groups" },
  { value: "questions", label: "Registration Questions" },
  { value: "deregistration-questions", label: "De-registration Questions" },
  { value: "inspection-questions", label: "Inspection Questions" },
] as const;

const QUESTION_TAB_VALUES = QUESTION_TABS.map((t) => t.value);
const DEFAULT_TAB = "registration-groups";

export function OperatorQuestionConfigurationPage() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { tab: searchTab } = Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(searchTab || DEFAULT_TAB);

  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>(
    () => sessionStorage.get(SESSION_KEYS.OPERATOR_SELECTED_JURISDICTIONS, []),
  );
  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () => sessionStorage.get(SESSION_KEYS.OPERATOR_SELECTED_LICENSE_TYPES, []),
  );

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.OPERATOR_SELECTED_JURISDICTIONS,
      selectedJurisdictions,
    );
  }, [selectedJurisdictions]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.OPERATOR_SELECTED_LICENSE_TYPES,
      selectedLicenseTypes,
    );
  }, [selectedLicenseTypes]);

  const { licenseTypeOptions, licenseTypes } = useLicenseTypeFilterOptions();
  const { filteredJurisdictions, jurisdictionsData } =
    useOperatorDashboardStats(
      isAuthSetupComplete,
      selectedJurisdictions,
      DASHBOARD_STATS_TAB_PRELOAD_ALL,
      selectedLicenseTypes,
    );

  const jurisdictionOptions =
    filteredJurisdictions?.map((j) => ({
      value: j.id,
      label: jurisdictionOptionLabel(j),
    })) ?? [];
  const jurisdictionCatalog = useMemo(
    () =>
      (jurisdictionsData?.jurisdictions ?? []).map((jurisdiction) => ({
        id: jurisdiction.id,
        name: jurisdiction.name,
      })),
    [jurisdictionsData?.jurisdictions],
  );

  useEffect(() => {
    if (searchTab && searchTab !== selectedTab) {
      setSelectedTab(searchTab);
    }
  }, [searchTab, selectedTab]);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    navigate({
      to: location.pathname,
      search: { tab: value },
      replace: true,
    });
  };

  const effectiveJurisdictions = selectedJurisdictions;
  /** License types that appear on selected jurisdictions' configs, intersected with the license type filter — used to scope question groups. */
  const questionGroupLicenseTypeIds = useMemo(() => {
    const allLtIds = licenseTypeOptions.map((o) => o.value);
    const effectiveLt =
      selectedLicenseTypes.length > 0 ? selectedLicenseTypes : allLtIds;

    if (selectedJurisdictions.length === 0) {
      return effectiveLt;
    }

    const selected = new Set(selectedJurisdictions);
    const fromJurisdictions = new Set<string>();
    for (const j of jurisdictionsData?.jurisdictions ?? []) {
      if (!selected.has(j.id)) continue;
      for (const c of j.license_type_configurations ?? []) {
        if (c.deleted_at) continue;
        fromJurisdictions.add(c.license_type_id);
      }
    }
    return effectiveLt.filter((id) => fromJurisdictions.has(id));
  }, [
    jurisdictionsData?.jurisdictions,
    selectedJurisdictions,
    selectedLicenseTypes,
    licenseTypeOptions,
  ]);

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  return (
    <div className={SETUP_PAGE_SHELL_CLASS}>
      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className={SETUP_PAGE_TABS_CLASS}
      >
        <SetupPageTabBar dashboardLink={getDashboardLink(UserType.OPERATOR)}>
          <TabsList className={SETUP_TABS_LIST_CLASS}>
            {QUESTION_TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                className={SETUP_TAB_TRIGGER_CLASS}
                value={value}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </SetupPageTabBar>

        <PageHeader
          className={SETUP_PAGE_CHROME_CLASS}
          title="Question Configuration"
          hideBackButton
          subtitle="Question groups, registration, de-registration and inspection questions"
          action={
            <DashboardFilters
              selectedJurisdictions={selectedJurisdictions}
              onSelectedJurisdictionsChange={setSelectedJurisdictions}
              jurisdictionOptions={jurisdictionOptions}
              showJurisdictionFilter={true}
              selectedLicenseTypes={selectedLicenseTypes}
              onSelectedLicenseTypesChange={setSelectedLicenseTypes}
              licenseTypeOptions={licenseTypeOptions}
              showLicenseTypeFilter={true}
            />
          }
        />
        <p className="shrink-0 text-muted-foreground text-sm mt-2">
          {
            {
              "registration-groups":
                "Group registration questions by license type for the registration flow.",
              "deregistration-groups":
                "Group de-registration questions by license type.",
              "inspection-groups":
                "Group inspection questions by license type.",
              questions:
                "Create and edit registration questions and their logic.",
              "deregistration-questions":
                "Create and edit de-registration questions and their logic.",
              "inspection-questions":
                "Create and edit inspection questions and their logic.",
            }[selectedTab]
          }
        </p>

        <TabsContent
          value="registration-groups"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <QuestionGroupConfigurationPanel
            questionType="registration"
            allowedLicenseTypeIds={
              selectedJurisdictions.length > 0
                ? questionGroupLicenseTypeIds
                : undefined
            }
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>

        <TabsContent
          value="deregistration-groups"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <QuestionGroupConfigurationPanel
            questionType="deregistration"
            allowedLicenseTypeIds={
              selectedJurisdictions.length > 0
                ? questionGroupLicenseTypeIds
                : undefined
            }
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>

        <TabsContent
          value="inspection-groups"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <QuestionGroupConfigurationPanel
            questionType="inspection"
            allowedLicenseTypeIds={
              selectedJurisdictions.length > 0
                ? questionGroupLicenseTypeIds
                : undefined
            }
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>

        <TabsContent value="questions" className={SETUP_PAGE_LIST_TAB_CLASS}>
          <DashboardQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            onSelectedJurisdictionsChange={setSelectedJurisdictions}
            jurisdictionCatalog={jurisdictionCatalog}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>

        <TabsContent
          value="deregistration-questions"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <DashboardDeregistrationQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            onSelectedJurisdictionsChange={setSelectedJurisdictions}
            jurisdictionCatalog={jurisdictionCatalog}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>

        <TabsContent
          value="inspection-questions"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <DashboardInspectionQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            onSelectedJurisdictionsChange={setSelectedJurisdictions}
            jurisdictionCatalog={jurisdictionCatalog}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypes}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
